import type { FragmentData } from "@gqlb/core";
import type React from "react";
import { b } from "@/b";
import { MEDIA_FRAGMENT } from "@/components/ResponsiveImage";
import { PreviouslyOrderedCard } from "./PreviouslyOrderedCard";

// ---------------------------------------------------------------------------
// GraphQL — fragment for previously ordered / reorderable items (SSR)
// ---------------------------------------------------------------------------

export const PreviouslyOrdered_Fragment = b.fragment("PreviouslyOrdered", "Query", (b) => [
  b.viewer((b) => [
    b.id(),
    b.account((b) => [
      b.id(),
      b.reorderableItems({ first: 20 }, (b) => [
        b.dedupeKey(),
        b.product((b) => [
          b.id(),
          b.name(),
          b.slug(),
          b.images((b) => [b.__fragment(MEDIA_FRAGMENT)]),
        ]),
        b.selections(),
        b.selectionSummaryLabels(),
      ]),
    ]),
  ]),
]);

/** Matches the shape of Account.reorderableItems from GraphQL. */
export type ReorderableItem = {
  dedupeKey: string;
  product: {
    id: string;
    name: string;
    slug: string;
    images: readonly FragmentData<typeof MEDIA_FRAGMENT>[];
  };
  selections: readonly (readonly string[])[];
  selectionSummaryLabels: readonly string[];
};

export const PreviouslyOrderedSection: React.FC<{
  items: ReorderableItem[];
  reorderingKey: string | null;
  onReorder: (item: ReorderableItem, info: { imageUrl: string; rect: DOMRect }) => void;
}> = ({ items, reorderingKey, onReorder }) => {
  if (items.length === 0) return null;

  return (
    <section id="previously-ordered" className="mb-14 md:mb-16 mt-8">
      {/* Header */}
      <div className="mb-6 px-6 lg:px-8">
        <div className="flex items-center gap-3">
          <h2 className="text-lg md:text-xl font-bold text-brand-black tracking-tight">
            Order Again
          </h2>
          <span className="text-xs font-medium text-gray-500 bg-gray-100 rounded-full px-2.5 py-0.5">
            {items.length} {items.length === 1 ? "item" : "items"}
          </span>
        </div>
        <p className="text-sm text-gray-500 mt-1">Reorder meals you loved</p>
      </div>

      {/* Horizontal scroll — matches MobileCarousel spacing */}
      <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2">
        <div className="shrink-0 w-4 lg:w-6" aria-hidden="true" />
        {items.map((item) => (
          <div key={item.dedupeKey} className="w-[260px] shrink-0">
            <PreviouslyOrderedCard
              item={item}
              isAdding={reorderingKey === item.dedupeKey}
              onReorder={onReorder}
            />
          </div>
        ))}
        <div className="shrink-0 w-4 lg:w-6" aria-hidden="true" />
      </div>
    </section>
  );
};
